export const topics = ['Getting started', 'Design and editing', 'AI tools', 'Personalization', 'Integrations', 'Publishing'] as const;
export type Topic = typeof topics[number];
export type Tutorial = {
  id: number;
  slug: string;
  title: string;
  vimeoUrl: string;
  vimeoId: string;
  duration: string;
  publicationDate: string;
  description1: string;
  description2: string;
  docsTitle: string;
  docsUrl: string;
  related: string[];
  mainThumbnail: string;
  thumbnail: string;
  shortDescription: string;
  topic: Topic;
};
export const tutorials: Tutorial[] = [
  {
    "id": 1,
    "slug": "start-creating-with-maker",
    "title": "Start creating with Maker",
    "vimeoUrl": "https://vimeo.com/1097293538",
    "vimeoId": "1097293538",
    "duration": "1:54",
    "publicationDate": "Jun 29, 2025",
    "description1": "Maker turns plain-language prompts into fully coded, responsive webpages and front-end experiences directly in your browser.",
    "description2": "Describe what you want to create, review the generated project in the live preview, and continue refining it with prompts or visual editing tools.",
    "docsTitle": "Quickstart",
    "docsUrl": "https://docs.maker.co/introduction/quickstart",
    "related": [
      "Build faster with Inspiration",
      "Add a style guide",
      "Use Chat Mode"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2051330003-0fa0737557ac870d63555d07134ec0ba5c841d807bf86320c1b5c1ce47047cea-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/start-creating-with-maker-012xbt4vqawa.png",
    "shortDescription": "Learn how to start a project, describe your idea, and generate your first experience with Maker.",
    "topic": "Getting started"
  },
  {
    "id": 2,
    "slug": "build-faster-with-inspiration",
    "title": "Build faster with Inspiration",
    "vimeoUrl": "https://vimeo.com/1158756618",
    "vimeoId": "1158756618",
    "duration": "1:23",
    "publicationDate": "Jan 27, 2026",
    "description1": "Inspiration gives you ready-made use cases and practical prompts for pages, sections, components, animations, and other common workflows.",
    "description2": "Use an existing example as a starting point, adapt the prompt to your needs, and move from an idea to a structured project faster.",
    "docsTitle": "Prompt examples and use cases",
    "docsUrl": "https://docs.maker.co/prompting/prompt-basics/prompt-examples-and-use-cases",
    "related": [
      "Start creating with Maker",
      "Use Chat Mode",
      "Create a project from an image"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2114103346-24f739bf80877560c612ca223626c4d6d9b350144292a42d37b86a13ac185a8e-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/build-faster-with-inspiration-bjpgty164vv.png",
    "shortDescription": "Discover how to explore ready-made use cases and prompts to start your next project faster.",
    "topic": "Getting started"
  },
  {
    "id": 3,
    "slug": "import-from-figma",
    "title": "Import from Figma",
    "vimeoUrl": "https://vimeo.com/1101616518",
    "vimeoId": "1101616518",
    "duration": "0:34",
    "publicationDate": "Mar 24, 2026",
    "description1": "Figma import lets you bring sections, frames, or complete page designs into Maker by pasting a Figma link directly into the prompt box.",
    "description2": "Maker converts your design into a responsive, editable project that you can continue refining with prompts and visual editing tools.",
    "docsTitle": "Figma",
    "docsUrl": "https://docs.maker.co/features/figma",
    "related": [
      "Add a style guide",
      "Create a project from an image",
      "Use the Design Panel"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2051313998-cec1abf7958ad0b70e70e494d51b5acc3806ff6a9ce46e4d58e6b4deaea4994a-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/import-from-figma-eku5hwror7n.png",
    "shortDescription": "Learn how to import Figma frames and turn your designs into responsive projects in Maker.",
    "topic": "Integrations"
  },
  {
    "id": 4,
    "slug": "add-a-style-guide",
    "title": "Add a style guide",
    "vimeoUrl": "https://vimeo.com/1097293500",
    "vimeoId": "1097293500",
    "duration": "1:25",
    "publicationDate": "Jun 29, 2025",
    "description1": "Style Guides give Maker reusable brand rules for colors, typography, imagery, spacing, and other visual decisions.",
    "description2": "Create a guide from a website, Figma file, uploaded brand assets, or an existing project, then reuse it to keep every project consistent.",
    "docsTitle": "Style Guide",
    "docsUrl": "https://docs.maker.co/features/style-guide",
    "related": [
      "Import from Figma",
      "Create and use components",
      "Give a project a makeover"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2051313108-0995dafaa3ea80fdd562cb33f6cd76b806322299948e679dd7de19de5e6b8a5d-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/add-a-style-guide-porpoycsxti.png",
    "shortDescription": "Learn to create a Style Guide and keep your brand consistent across different projects.",
    "topic": "Design and editing"
  },
  {
    "id": 5,
    "slug": "use-the-design-panel",
    "title": "Use the Design Panel",
    "vimeoUrl": "https://vimeo.com/1145329323",
    "vimeoId": "1145329323",
    "duration": "1:08",
    "publicationDate": "Jan 27, 2026",
    "description1": "The Design Panel lets you fine-tune the appearance and layout of individual elements directly from the project preview.",
    "description2": "Adjust content, layout, typography, spacing, backgrounds, borders, and responsive styles without relying only on prompts.",
    "docsTitle": "Design Panel",
    "docsUrl": "https://docs.maker.co/features/design-panel",
    "related": [
      "Select elements",
      "Add animations",
      "Create and use components"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2094253373-5dda4bf50c521716cbb87601b9c89a127186443133f421863f8492d17b882469-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/use-the-design-panel-mwfgryeium.png",
    "shortDescription": "Learn how to adjust layouts, typography, colors, spacing, and other styles directly in the preview.",
    "topic": "Design and editing"
  },
  {
    "id": 6,
    "slug": "select-elements",
    "title": "Select elements",
    "vimeoUrl": "https://vimeo.com/1097293557",
    "vimeoId": "1097293557",
    "duration": "1:02",
    "publicationDate": "Jun 29, 2025",
    "description1": "Select to Edit lets you target a specific element in the live preview before asking Maker to make a change.",
    "description2": "Focus prompts and visual edits on a single part of your project without affecting the rest of the page.",
    "docsTitle": "Select to Edit",
    "docsUrl": "https://docs.maker.co/features/select-to-edit",
    "related": [
      "Use the Design Panel",
      "Add animations",
      "Generate images and videos"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2051353161-ac847d9b0951c1c731263cf89f9e7f3a54ca416cfb0242827d4f98e61dde3e70-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/select-elements-yr0wjr12i9k.png",
    "shortDescription": "Learn to select individual elements and make focused changes using prompts or the Design Panel.",
    "topic": "Design and editing"
  },
  {
    "id": 7,
    "slug": "create-and-manage-variants",
    "title": "Create and manage variants",
    "vimeoUrl": "https://vimeo.com/1169582882",
    "vimeoId": "1169582882",
    "duration": "2:06",
    "publicationDate": "Mar 2, 2026",
    "description1": "Variants let you create multiple versions of a project and control which experience your users see based on rules you define.",
    "description2": "Manage every version inside a single workflow, making testing, experimentation, and personalization simple without duplicating your project.",
    "docsTitle": "Variants",
    "docsUrl": "https://docs.maker.co/features/variants",
    "related": [
      "Personalize content by location",
      "Create design mocks",
      "Publish and share a project"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2128386304-4ff85d06e6384a52c2d75e28ded5e3d520236482d5bc3709e7743e55c8a7cac2-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/create-and-manage-variants-aez8rpseub.png",
    "shortDescription": "Learn how to create Variants and manage different experiences from one project.",
    "topic": "Personalization"
  },
  {
    "id": 8,
    "slug": "use-chat-mode",
    "title": "Use Chat Mode",
    "vimeoUrl": "https://vimeo.com/1150322645",
    "vimeoId": "1150322645",
    "duration": "1:27",
    "publicationDate": "Dec 30, 2025",
    "description1": "Chat Mode lets you discuss ideas, ask questions, and plan changes with Maker before making updates to your project.",
    "description2": "Use conversations to refine your direction, explore alternatives, and gain confidence before generating or editing your design.",
    "docsTitle": "Chat Mode",
    "docsUrl": "https://docs.maker.co/features/chat-mode",
    "related": [
      "Start creating with Maker",
      "Build faster with Inspiration",
      "Create with AI+ Content"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2101191739-3477003974e2d7e6a171f3908870d7bec0067c98f7283bbcb837961aa8ac4b0e-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/use-chat-mode-an0eeq4pyxm.png",
    "shortDescription": "Discover how to ask questions, explore ideas, and clarify changes before updating your project.",
    "topic": "AI tools"
  },
  {
    "id": 9,
    "slug": "generate-images-and-videos",
    "title": "Generate images and videos",
    "vimeoUrl": "https://vimeo.com/1128774132",
    "vimeoId": "1128774132",
    "duration": "1:19",
    "publicationDate": "Oct 20, 2025",
    "description1": "Generate original images and videos directly inside Maker using simple natural-language prompts.",
    "description2": "Create, edit, and refine visual assets without leaving your workflow, then instantly add them to your project.",
    "docsTitle": "Image and Video Generation",
    "docsUrl": "https://docs.maker.co/features/image-video-generation",
    "related": [
      "Create a project from an image",
      "Create with AI+ Content",
      "Select elements"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2094250330-057a40aceec65072c82217e66d9659215c7341a82a22880b397c4ec028cf4797-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/generate-images-and-videos-tma5zjn0f89.png",
    "shortDescription": "Learn to generate and edit images and videos directly inside your Maker projects.",
    "topic": "AI tools"
  },
  {
    "id": 10,
    "slug": "create-and-use-components",
    "title": "Create and use components",
    "vimeoUrl": "https://vimeo.com/1145946645",
    "vimeoId": "1145946645",
    "duration": "1:17",
    "publicationDate": "Dec 12, 2025",
    "description1": "Components let you create reusable building blocks that can be shared across projects and Style Guides.",
    "description2": "Update a component once and reuse it consistently throughout your work to speed up production and maintain design consistency.",
    "docsTitle": "Components",
    "docsUrl": "https://docs.maker.co/features/components",
    "related": [
      "Add a style guide",
      "Use the Design Panel",
      "Create design mocks"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2095138438-2b25a274698ccd33532ba517d327ab01dc84e3e0c060990ad0a81198bb176dab-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/create-and-use-components-n7uf7nr87h.png",
    "shortDescription": "Learn how to create reusable components and use them consistently across projects.",
    "topic": "Design and editing"
  },
  {
    "id": 11,
    "slug": "add-animations",
    "title": "Add animations",
    "vimeoUrl": "https://vimeo.com/1097293511",
    "vimeoId": "1097293511",
    "duration": "0:58",
    "publicationDate": "Jun 29, 2025",
    "description1": "Bring your project to life with animations that add movement, transitions, and interactive effects.",
    "description2": "Describe the animation you want, preview the result, and refine it until it matches your intended experience.",
    "docsTitle": "Animations",
    "docsUrl": "https://docs.maker.co/features/animations",
    "related": [
      "Use the Design Panel",
      "Select elements",
      "Generate images and videos"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2051354987-ae6de596dce3a021cf677093dcf9eab1002a65c610352b730b40b079f4d88295-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/add-animations-9iark6gwts9.png",
    "shortDescription": "Learn to add animations and interactions that make your project more dynamic and engaging.",
    "topic": "Design and editing"
  },
  {
    "id": 12,
    "slug": "connect-external-tools",
    "title": "Connect external tools",
    "vimeoUrl": "https://vimeo.com/1148616585",
    "vimeoId": "1148616585",
    "duration": "1:14",
    "publicationDate": "Dec 22, 2025",
    "description1": "Connect Maker to your favorite tools and services to access content, assets, and data without leaving your project.",
    "description2": "Bring information from supported platforms directly into your workflow to streamline content creation and collaboration.",
    "docsTitle": "Connectors",
    "docsUrl": "https://docs.maker.co/features/connectors",
    "related": [
      "Use Chat Mode",
      "Generate images and videos",
      "Enhance an existing project"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2098879889-2c562eae3f3c442942dceae16399cae67b9bddaa34d2388fc85d036046c1d306-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/connect-external-tools-k58i5vtes9.png",
    "shortDescription": "Discover how to connect external tools and bring content, data, and media directly into Maker.",
    "topic": "Integrations"
  },
  {
    "id": 13,
    "slug": "create-design-mocks",
    "title": "Create design mocks",
    "vimeoUrl": "https://vimeo.com/1192296901",
    "vimeoId": "1192296901",
    "duration": "1:20",
    "publicationDate": "May 14, 2026",
    "description1": "Generate multiple design concepts from a single brief and quickly explore different creative directions.",
    "description2": "Compare visual approaches, choose the strongest concept, and continue building it as a fully editable Maker project.",
    "docsTitle": "Design Mocks",
    "docsUrl": "https://docs.maker.co/features/design-mocks",
    "related": [
      "Create with AI+ Content",
      "Give a project a makeover",
      "Create and use components"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2157516569-73d8d19475eb928f7ba20ce966e355aa1924fc1a90d9e3efdc9d89f86adf3d57-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/create-design-mocks-d75cckagn3v.png",
    "shortDescription": "Learn how to generate multiple design directions and explore visual concepts before building your project.",
    "topic": "Design and editing"
  },
  {
    "id": 14,
    "slug": "personalize-content-by-location",
    "title": "Personalize content by location",
    "vimeoUrl": "https://vimeo.com/1195376966",
    "vimeoId": "1195376966",
    "duration": "1:21",
    "publicationDate": "May 25, 2026",
    "description1": "Show different content based on where your visitors are located using built-in location rules.",
    "description2": "Deliver more relevant messaging, offers, and experiences to different audiences without creating separate projects.",
    "docsTitle": "Location-based Personalization",
    "docsUrl": "https://docs.maker.co/features/location-based-personalization",
    "related": [
      "Create and manage variants",
      "Publish and share a project",
      "Create with AI+ Content"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2161237519-35374e0218c462aabbbb124210954bd3e9eb5665c5de86332094bd1a541d0943-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/personalize-content-by-location-sj0k5v7shh.png",
    "shortDescription": "Learn to personalize content by location and show relevant experiences to different audiences.",
    "topic": "Personalization"
  },
  {
    "id": 15,
    "slug": "create-with-ai-content",
    "title": "Create with AI+ Content",
    "vimeoUrl": "https://vimeo.com/1162174959",
    "vimeoId": "1162174959",
    "duration": "1:24",
    "publicationDate": "Feb 5, 2026",
    "description1": "AI+ Content transforms an existing product page into rich, on-brand product content with enhanced layouts, visuals, and copy.",
    "description2": "Choose a template, provide your product details, and let Maker generate high-quality PDP content that you can edit and publish.",
    "docsTitle": "AI+ PDP Content",
    "docsUrl": "https://docs.maker.co/features/ai+-pdp-content",
    "related": [
      "Create design mocks",
      "Generate images and videos",
      "Add a style guide"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2137783068-4245d4f8d9794950798cad8f2d92ff962720adf8ea55a1937dc8c5af3ba2a6b7-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/create-with-ai-plus-content-u4a3nqk9c3.png",
    "shortDescription": "Discover how to generate enhanced, on-brand product content from an existing product page.",
    "topic": "AI tools"
  },
  {
    "id": 16,
    "slug": "give-a-project-a-makeover",
    "title": "Give a project a makeover",
    "vimeoUrl": "https://vimeo.com/1112927232",
    "vimeoId": "1112927232",
    "duration": "1:02",
    "publicationDate": "Aug 25, 2025",
    "description1": "Makeover lets you instantly redesign an existing Maker project while preserving its content and overall structure.",
    "description2": "Explore a completely new visual direction, refresh your design, and continue refining the updated version with Maker's editing tools.",
    "docsTitle": "Makeover",
    "docsUrl": "https://docs.maker.co/features/makeover",
    "related": [
      "Create design mocks",
      "Add a style guide",
      "Remix a project"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2051312363-31d15d18f80994de0c47043a329b4e206457c4123fe2600af33dc1723d66bdcd-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/give-a-project-a-makeover-ybmsq3iik9e.png",
    "shortDescription": "Learn how to instantly redesign an existing page and explore a fresh visual direction.",
    "topic": "Getting started"
  },
  {
    "id": 17,
    "slug": "publish-and-share-a-project",
    "title": "Publish and share a project",
    "vimeoUrl": "https://vimeo.com/1097293527",
    "vimeoId": "1097293527",
    "duration": "1:12",
    "publicationDate": "Mar 24, 2026",
    "description1": "Publish your project with a single click and generate a shareable link that anyone can access.",
    "description2": "Preview your latest changes, publish new versions whenever you're ready, and easily share your work with teammates or clients.",
    "docsTitle": "Publishing",
    "docsUrl": "https://docs.maker.co/features/publish",
    "related": [
      "Connect a custom domain",
      "Personalize content by location",
      "Start creating with Maker"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2051355336-3792ba89d98d370b6c2b40d315f153b314d5393e6882e2724f4f1de82c411957-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/publish-and-share-a-project-6ztb6jisa0s.png",
    "shortDescription": "Learn to publish your project and create a shareable link for others to view.",
    "topic": "Publishing"
  },
  {
    "id": 18,
    "slug": "connect-a-custom-domain",
    "title": "Connect a custom domain",
    "vimeoUrl": "https://vimeo.com/1128775124",
    "vimeoId": "1128775124",
    "duration": "0:54",
    "publicationDate": "Oct 20, 2025",
    "description1": "Connect your own domain to publish Maker projects under your brand instead of using a Maker URL.",
    "description2": "Verify your domain, assign projects to it, and deliver a seamless branded experience for your visitors.",
    "docsTitle": "Custom Domains",
    "docsUrl": "https://docs.maker.co/tips-and-tricks/custom-domain",
    "related": [
      "Publish and share a project",
      "Enhance an existing project",
      "Connect external tools"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2072970713-b5d1b269d53f8968da4e1513ba710bd85b5db0db6fde711b86085b5bba7679e9-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/connect-a-custom-domain-s5gqja6cpt8.png",
    "shortDescription": "Learn how to connect a custom domain and publish your Maker project on your own URL.",
    "topic": "Publishing"
  },
  {
    "id": 19,
    "slug": "create-a-project-from-an-image",
    "title": "Create a project from an image",
    "vimeoUrl": "https://vimeo.com/1113137586",
    "vimeoId": "1113137586",
    "duration": "0:46",
    "publicationDate": "Aug 26, 2025",
    "description1": "Turn a screenshot, mockup, or design reference into a fully editable, responsive Maker project.",
    "description2": "Upload an image, let Maker recreate its structure, and continue editing the generated project using prompts and visual tools.",
    "docsTitle": "Create from Image",
    "docsUrl": "https://docs.maker.co/features/create-from-image",
    "related": [
      "Import from Figma",
      "Generate images and videos",
      "Give a project a makeover"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2052020497-78740691c18c9ff83c4bed6de3090dde18ad6af55c1173dc7d93e433a0b36cea-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/create-a-project-from-an-image-5759c67cmiq.png",
    "shortDescription": "Discover how to turn a reference image into an editable, responsive project in Maker.",
    "topic": "Getting started"
  },
  {
    "id": 20,
    "slug": "remix-a-project",
    "title": "Remix a project",
    "vimeoUrl": "https://vimeo.com/1146578809",
    "vimeoId": "1146578809",
    "duration": "0:59",
    "publicationDate": "Dec 15, 2025",
    "description1": "Remix creates a copy of an existing Maker project so you can experiment without changing the original.",
    "description2": "Use Remix to explore new ideas, create alternative versions, or start from an existing project instead of building from scratch.",
    "docsTitle": "Project Remix",
    "docsUrl": "https://docs.maker.co/features/project-remix",
    "related": [
      "Give a project a makeover",
      "Build faster with Inspiration",
      "Create a project from an image"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2096064734-f930aafcb99a0fbc446c02c4ab4f2b2c3a29a53b93c92bccd549ed692032b60c-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/remix-a-project-b32nb3bnev7.png",
    "shortDescription": "Learn to copy and customize an existing project without changing the original version.",
    "topic": "Getting started"
  },
  {
    "id": 21,
    "slug": "enhance-an-existing-project",
    "title": "Enhance an existing project",
    "vimeoUrl": "https://vimeo.com/1112925424",
    "vimeoId": "1112925424",
    "duration": "1:01",
    "publicationDate": "Aug 25, 2025",
    "description1": "Bring an existing website or project into Maker and continue improving it with AI-powered editing and visual tools.",
    "description2": "Add new sections, update content, redesign layouts, and extend your project without rebuilding it from scratch.",
    "docsTitle": "Enhance your project with Maker",
    "docsUrl": "https://docs.maker.co/tips-and-tricks/from-idea-to-live",
    "related": [
      "Connect external tools",
      "Publish and share a project",
      "Connect a custom domain"
    ],
    "mainThumbnail": "https://i.vimeocdn.com/video/2051734544-0885d16d3556d653a9ea936b3f7bdb02861d040edf18cc2ec891d2d11320e37e-d_1280?region=us",
    "thumbnail": "https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/enhance-an-existing-project-updated-rarme8jgtm.png",
    "shortDescription": "Discover how to add Maker content to an existing page and control where it appears.",
    "topic": "Getting started"
  }
];
